import { useState } from "react";
import { Mail, AlertTriangle, CheckCircle, XCircle, Loader2, Sparkles, Plus, Trash2, ArrowUpDown, Download, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface BulkSubjectLineAnalyzerProps {
  userEmail?: string | null;
  userName?: string | null;
  sessionToken?: string | null;
}

interface AnalysisResult {
  score: number;
  risk: "low" | "medium" | "high";
  triggers: { word: string; category: string; severity: string }[];
  suggestions: string[];
  improvedVersion: string;
}

interface SubjectLineResult {
  subjectLine: string;
  result: AnalysisResult | null;
  isAnalyzing: boolean;
  error: string | null;
}

type SortBy = "original" | "score-asc" | "score-desc";

export const BulkSubjectLineAnalyzer = ({ userEmail, userName, sessionToken }: BulkSubjectLineAnalyzerProps) => {
  const [subjectLines, setSubjectLines] = useState<SubjectLineResult[]>([
    { subjectLine: "", result: null, isAnalyzing: false, error: null },
  ]);
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sortBy, setSortBy] = useState<SortBy>("original");

  const sampleSubjects = [
    "🔥 URGENT: You Won $1000 FREE - Claim NOW!!!",
    "Quick question about your project",
    "LIMITED TIME: 90% OFF Everything - Act Fast!",
    "Meeting follow-up: Next steps for Q1 planning",
    "Congratulations! You've Been Selected as a Winner",
    "Your invoice is ready",
    "Re: Our conversation yesterday",
    "Don't miss out on this amazing opportunity!",
  ];

  const loadSamples = () => {
    const samples = sampleSubjects.slice(0, 5).map((subject) => ({
      subjectLine: subject,
      result: null,
      isAnalyzing: false,
      error: null,
    }));
    setSubjectLines(samples);
    setSortBy("original");
  };

  const addSubjectLine = () => {
    if (subjectLines.length >= 10) {
      toast.error("Maximum 10 subject lines allowed");
      return;
    }
    setSubjectLines([...subjectLines, { subjectLine: "", result: null, isAnalyzing: false, error: null }]);
  };

  const removeSubjectLine = (index: number) => {
    if (subjectLines.length === 1) return;
    setSubjectLines(subjectLines.filter((_, i) => i !== index));
  };

  const updateSubjectLine = (index: number, value: string) => {
    const updated = [...subjectLines];
    updated[index] = { ...updated[index], subjectLine: value, result: null, error: null };
    setSubjectLines(updated);
  };

  const analyzeAll = async () => {
    const validLines = subjectLines.filter((s) => s.subjectLine.trim().length > 0);
    if (validLines.length === 0) {
      toast.error("Please enter at least one subject line");
      return;
    }

    setIsAnalyzingAll(true);
    setProgress(0);
    setSortBy("original");

    const updated = subjectLines.map((s) => ({
      ...s,
      isAnalyzing: s.subjectLine.trim().length > 0,
      result: null,
      error: null,
    }));
    setSubjectLines(updated);

    let completed = 0;
    const total = validLines.length;

    const analyzeOne = async (index: number) => {
      if (!subjectLines[index].subjectLine.trim()) return;

      try {
        const { data, error } = await supabase.functions.invoke("analyze-subject-line", {
          body: { 
            subjectLine: subjectLines[index].subjectLine.trim(),
            analyzeType: "subject" 
          },
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        setSubjectLines((prev) => {
          const newList = [...prev];
          newList[index] = { ...newList[index], result: data, isAnalyzing: false };
          return newList;
        });
      } catch (error) {
        console.error("Error analyzing subject line:", error);
        setSubjectLines((prev) => {
          const newList = [...prev];
          newList[index] = { ...newList[index], error: "Failed to analyze", isAnalyzing: false };
          return newList;
        });
      } finally {
        completed++;
        setProgress((completed / total) * 100);
      }
    };

    // Analyze sequentially to avoid rate limits
    for (let i = 0; i < subjectLines.length; i++) {
      await analyzeOne(i);
    }

    setIsAnalyzingAll(false);
    toast.success(`Analyzed ${total} subject line${total > 1 ? "s" : ""}`);
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low": return "text-success";
      case "medium": return "text-warning";
      case "high": return "text-destructive";
      default: return "text-muted-foreground";
    }
  };

  const getRiskBg = (risk: string) => {
    switch (risk) {
      case "low": return "bg-success/10 border-success/20";
      case "medium": return "bg-warning/10 border-warning/20";
      case "high": return "bg-destructive/10 border-destructive/20";
      default: return "bg-muted border-border";
    }
  };

  const getRiskIcon = (risk: string) => {
    switch (risk) {
      case "low": return <CheckCircle className="w-4 h-4 text-success" />;
      case "medium": return <AlertTriangle className="w-4 h-4 text-warning" />;
      case "high": return <XCircle className="w-4 h-4 text-destructive" />;
      default: return null;
    }
  };

  const getDisplayLines = () => {
    const lines = [...subjectLines];
    if (sortBy === "score-asc") {
      return lines.sort((a, b) => {
        if (!a.result && !b.result) return 0;
        if (!a.result) return 1;
        if (!b.result) return -1;
        return a.result.score - b.result.score;
      });
    }
    if (sortBy === "score-desc") {
      return lines.sort((a, b) => {
        if (!a.result && !b.result) return 0;
        if (!a.result) return 1;
        if (!b.result) return -1;
        return b.result.score - a.result.score;
      });
    }
    return lines;
  };

  const hasResults = subjectLines.some((s) => s.result !== null);
  const analyzedCount = subjectLines.filter((s) => s.result !== null).length;

  const generateCSV = () => {
    const headers = ["#", "Subject Line", "Score", "Risk Level", "Triggers", "Suggested Improvement"];
    const rows = subjectLines
      .filter((s) => s.result)
      .map((item, index) => [
        index + 1,
        `"${item.subjectLine.replace(/"/g, '""')}"`,
        item.result!.score,
        item.result!.risk.toUpperCase(),
        `"${item.result!.triggers.map((t) => t.word).join(", ")}"`,
        `"${(item.result!.improvedVersion || "").replace(/"/g, '""')}"`,
      ]);

    const csvContent = [
      "Bulk Subject Line Analysis Report - IT Dukes",
      `Generated: ${new Date().toLocaleString()}`,
      `Total Analyzed: ${analyzedCount}`,
      "",
      headers.join(","),
      ...rows.map((row) => row.join(",")),
      "",
      "Summary Statistics",
      `Best Score,${Math.min(...subjectLines.filter((s) => s.result).map((s) => s.result!.score))}`,
      `Average Score,${Math.round(subjectLines.filter((s) => s.result).reduce((sum, s) => sum + s.result!.score, 0) / analyzedCount)}`,
      `Worst Score,${Math.max(...subjectLines.filter((s) => s.result).map((s) => s.result!.score))}`,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `subject-line-analysis-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("CSV exported successfully");
  };

  const generatePDF = () => {
    const bestScore = Math.min(...subjectLines.filter((s) => s.result).map((s) => s.result!.score));
    const avgScore = Math.round(subjectLines.filter((s) => s.result).reduce((sum, s) => sum + s.result!.score, 0) / analyzedCount);
    const worstScore = Math.max(...subjectLines.filter((s) => s.result).map((s) => s.result!.score));

    const results = subjectLines
      .filter((s) => s.result)
      .map((item, index) => `
        <tr style="border-bottom: 1px solid #e5e7eb;">
          <td style="padding: 12px 8px; font-weight: 500;">${index + 1}</td>
          <td style="padding: 12px 8px; max-width: 250px; word-wrap: break-word;">${item.subjectLine}</td>
          <td style="padding: 12px 8px; text-align: center;">
            <span style="display: inline-block; padding: 4px 12px; border-radius: 20px; font-weight: 600; background: ${item.result!.risk === 'low' ? '#dcfce7' : item.result!.risk === 'medium' ? '#fef3c7' : '#fecaca'}; color: ${item.result!.risk === 'low' ? '#166534' : item.result!.risk === 'medium' ? '#92400e' : '#b91c1c'};">
              ${item.result!.score}/100
            </span>
          </td>
          <td style="padding: 12px 8px; text-align: center; text-transform: uppercase; font-size: 11px; font-weight: 600; color: ${item.result!.risk === 'low' ? '#166534' : item.result!.risk === 'medium' ? '#92400e' : '#b91c1c'};">
            ${item.result!.risk}
          </td>
          <td style="padding: 12px 8px; font-size: 12px; color: #6b7280;">${item.result!.triggers.slice(0, 3).map((t) => t.word).join(", ") || "None"}</td>
          <td style="padding: 12px 8px; font-size: 12px; color: #059669; font-style: italic;">${item.result!.improvedVersion || "-"}</td>
        </tr>
      `)
      .join("");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Bulk Subject Line Analysis Report</title>
        <style>
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 40px; background: #fff; color: #1f2937; }
          .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #6366f1; }
          .logo { display: flex; align-items: center; gap: 12px; }
          .logo-icon { width: 48px; height: 48px; background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 20px; }
          .company-name { font-size: 24px; font-weight: 700; color: #1f2937; }
          .tagline { font-size: 12px; color: #6b7280; }
          .report-title { font-size: 28px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
          .report-date { font-size: 14px; color: #6b7280; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 30px 0; }
          .summary-card { background: #f9fafb; border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #e5e7eb; }
          .summary-value { font-size: 36px; font-weight: 700; margin-bottom: 4px; }
          .summary-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
          .best { color: #166534; }
          .avg { color: #92400e; }
          .worst { color: #b91c1c; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 13px; }
          th { background: #f3f4f6; padding: 14px 8px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb; }
          .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
          .footer a { color: #6366f1; text-decoration: none; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">
            <div class="logo-icon">ID</div>
            <div>
              <div class="company-name">IT Dukes</div>
              <div class="tagline">Email Deliverability Experts</div>
            </div>
          </div>
          <div style="text-align: right;">
            <div class="report-title">Subject Line Analysis</div>
            <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-value best">${bestScore}</div>
            <div class="summary-label">Best Score</div>
          </div>
          <div class="summary-card">
            <div class="summary-value avg">${avgScore}</div>
            <div class="summary-label">Average Score</div>
          </div>
          <div class="summary-card">
            <div class="summary-value worst">${worstScore}</div>
            <div class="summary-label">Worst Score</div>
          </div>
        </div>

        <h3 style="font-size: 18px; font-weight: 600; color: #374151; margin-bottom: 16px;">Detailed Results (${analyzedCount} Subject Lines)</h3>
        
        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Subject Line</th>
              <th style="width: 80px; text-align: center;">Score</th>
              <th style="width: 70px; text-align: center;">Risk</th>
              <th style="width: 150px;">Triggers</th>
              <th style="width: 200px;">Suggested Improvement</th>
            </tr>
          </thead>
          <tbody>
            ${results}
          </tbody>
        </table>

        <div class="footer">
          <p>This report was generated by <a href="https://insights.itdukes.com/tools">IT Dukes Email Tools</a></p>
          <p style="margin-top: 8px;">Lower spam scores indicate better deliverability potential.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
      toast.success("PDF ready for download");
    } else {
      toast.error("Please allow popups to download PDF");
    }
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Bulk Subject Line Analyzer
          </h3>
          <p className="text-sm text-muted-foreground">
            Compare multiple subject lines and find the best performer
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Header Actions */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={loadSamples} className="text-xs text-primary">
            Load Samples
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{subjectLines.length}/10 lines</span>
            <Button
              variant="outline"
              size="sm"
              onClick={addSubjectLine}
              disabled={subjectLines.length >= 10}
              className="text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Line
            </Button>
          </div>
        </div>

        {/* Subject Line Inputs */}
        <div className="space-y-3">
          {subjectLines.map((item, index) => (
            <div key={index} className="relative">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
                    {item.result && (
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRiskBg(item.result.risk)}`}>
                        {getRiskIcon(item.result.risk)}
                        <span className={getRiskColor(item.result.risk)}>{item.result.score}/100</span>
                      </div>
                    )}
                    {item.isAnalyzing && (
                      <Loader2 className="w-3 h-3 animate-spin text-primary" />
                    )}
                    {item.error && (
                      <span className="text-xs text-destructive">{item.error}</span>
                    )}
                  </div>
                  <Textarea
                    value={item.subjectLine}
                    onChange={(e) => updateSubjectLine(index, e.target.value)}
                    placeholder="Enter subject line..."
                    className="min-h-[44px] resize-none text-sm"
                    maxLength={200}
                    disabled={isAnalyzingAll}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeSubjectLine(index)}
                  disabled={subjectLines.length === 1 || isAnalyzingAll}
                  className="text-muted-foreground hover:text-destructive mt-5"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        {isAnalyzingAll && (
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground text-center">
              Analyzing... {Math.round(progress)}%
            </p>
          </div>
        )}

        {/* Analyze Button */}
        <Button
          onClick={analyzeAll}
          disabled={isAnalyzingAll || subjectLines.every((s) => !s.subjectLine.trim())}
          className="w-full"
        >
          {isAnalyzingAll ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing {subjectLines.filter((s) => s.subjectLine.trim()).length} Subject Lines...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Analyze All Subject Lines
            </>
          )}
        </Button>

        {/* Results Comparison */}
        {hasResults && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Sort & Export Controls */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium text-foreground">
                  Comparison Results ({analyzedCount} analyzed)
                </h4>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                      <Download className="w-3 h-3" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={generateCSV} className="gap-2 cursor-pointer">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={generatePDF} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4 text-red-500" />
                      Export as PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant={sortBy === "original" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setSortBy("original")}
                  className="text-xs h-7 px-2"
                >
                  Original
                </Button>
                <Button
                  variant={sortBy === "score-asc" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setSortBy("score-asc")}
                  className="text-xs h-7 px-2"
                >
                  <ArrowUpDown className="w-3 h-3 mr-1" />
                  Best First
                </Button>
                <Button
                  variant={sortBy === "score-desc" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setSortBy("score-desc")}
                  className="text-xs h-7 px-2"
                >
                  <ArrowUpDown className="w-3 h-3 mr-1" />
                  Worst First
                </Button>
              </div>
            </div>

            {/* Results List */}
            <div className="space-y-3">
              {getDisplayLines().map((item, displayIndex) => {
                if (!item.result) return null;
                const originalIndex = subjectLines.findIndex((s) => s === item);
                
                return (
                  <div
                    key={originalIndex}
                    className={`rounded-xl border p-4 ${getRiskBg(item.result.risk)} transition-all`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {getRiskIcon(item.result.risk)}
                          <span className={`text-lg font-bold ${getRiskColor(item.result.risk)}`}>
                            {item.result.score}/100
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${getRiskColor(item.result.risk)} bg-background/50`}>
                            {item.result.risk} Risk
                          </span>
                        </div>
                        <p className="text-sm text-foreground truncate mb-2" title={item.subjectLine}>
                          "{item.subjectLine}"
                        </p>
                        
                        {/* Triggers */}
                        {item.result.triggers.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {item.result.triggers.slice(0, 3).map((trigger, i) => (
                              <span
                                key={i}
                                className="inline-flex px-2 py-0.5 rounded text-xs bg-background/50 text-muted-foreground border border-border"
                              >
                                {trigger.word}
                              </span>
                            ))}
                            {item.result.triggers.length > 3 && (
                              <span className="text-xs text-muted-foreground">
                                +{item.result.triggers.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Improved Version */}
                        {item.result.improvedVersion && (
                          <div className="mt-2 p-2 bg-success/5 rounded-lg border border-success/10">
                            <p className="text-xs text-muted-foreground mb-1">Suggested improvement:</p>
                            <p className="text-sm text-success">"{item.result.improvedVersion}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Stats */}
            {analyzedCount >= 2 && (
              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <h5 className="text-sm font-medium text-foreground mb-3">Summary</h5>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-success">
                      {Math.min(...subjectLines.filter((s) => s.result).map((s) => s.result!.score))}
                    </div>
                    <div className="text-xs text-muted-foreground">Best Score</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-warning">
                      {Math.round(
                        subjectLines.filter((s) => s.result).reduce((sum, s) => sum + s.result!.score, 0) /
                          analyzedCount
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">Average</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-destructive">
                      {Math.max(...subjectLines.filter((s) => s.result).map((s) => s.result!.score))}
                    </div>
                    <div className="text-xs text-muted-foreground">Worst Score</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
